import { useCallback, useMemo, useState } from "react";
import { defaultBookmarks } from "../data/bookmarks";
import type { Bookmark, BookmarkDraft } from "../types";

const storageKey = "lab-bookmark.items.v1";

const normalizeUrlKey = (url: string) => url.trim().replace(/\/+$/, "").toLowerCase();

const mergeMissingDefaults = (stored: Bookmark[]) => {
  const existingIds = new Set(stored.map((bookmark) => bookmark.id));
  const existingUrls = new Set(stored.map((bookmark) => normalizeUrlKey(bookmark.url)));
  const missingDefaults = defaultBookmarks.filter(
    (bookmark) => !existingIds.has(bookmark.id) && !existingUrls.has(normalizeUrlKey(bookmark.url))
  );

  return missingDefaults.length > 0 ? [...stored, ...missingDefaults] : stored;
};

const readStoredBookmarks = (): Bookmark[] => {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return defaultBookmarks;
    const parsed = JSON.parse(raw) as Bookmark[];
    if (!Array.isArray(parsed) || parsed.length === 0) return defaultBookmarks;

    const merged = mergeMissingDefaults(parsed);
    if (merged.length !== parsed.length) persist(merged);
    return merged;
  } catch {
    return defaultBookmarks;
  }
};

const persist = (bookmarks: Bookmark[]) => {
  window.localStorage.setItem(storageKey, JSON.stringify(bookmarks));
};

const moveById = (items: Bookmark[], activeId: string, overId: string) => {
  const from = items.findIndex((item) => item.id === activeId);
  const to = items.findIndex((item) => item.id === overId);
  if (from < 0 || to < 0 || from === to) return items;
  const next = [...items];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

export function useBookmarks() {
  const [bookmarks, setBookmarks] = useState<Bookmark[]>(readStoredBookmarks);

  const saveBookmark = useCallback((draft: BookmarkDraft) => {
    const normalized: Bookmark = {
      id: draft.id ?? crypto.randomUUID(),
      label: draft.label.trim(),
      url: draft.url.trim(),
      category: draft.category,
      icon: draft.icon,
      accent: draft.accent,
      description: draft.description?.trim(),
      status: draft.status ?? "unknown",
      customIconDataUrl: draft.customIconDataUrl,
      iconSourceUrl: draft.iconSourceUrl
    };

    setBookmarks((current) => {
      const next = current.some((item) => item.id === normalized.id)
        ? current.map((item) => (item.id === normalized.id ? normalized : item))
        : [normalized, ...current];
      persist(next);
      return next;
    });
  }, []);

  const deleteBookmark = useCallback((id: string) => {
    setBookmarks((current) => {
      const next = current.filter((item) => item.id !== id);
      persist(next);
      return next;
    });
  }, []);

  const resetBookmarks = useCallback(() => {
    persist(defaultBookmarks);
    setBookmarks(defaultBookmarks);
  }, []);

  const reorderBookmarks = useCallback((activeId: string, overId: string) => {
    setBookmarks((current) => {
      const next = moveById(current, activeId, overId);
      persist(next);
      return next;
    });
  }, []);

  return useMemo(
    () => ({ bookmarks, saveBookmark, deleteBookmark, resetBookmarks, reorderBookmarks }),
    [bookmarks, deleteBookmark, reorderBookmarks, resetBookmarks, saveBookmark]
  );
}
